import type { SupplierPayables } from "@hisense/shared";
import { formatMoney } from "@/domain/money";
import { printHtml } from "./printWindow";

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

const longDate = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

/**
 * Bảng kê công nợ phải trả, bản in cho nhà cung cấp (A4).
 *
 * Dựng từ ĐÚNG dữ liệu máy chủ đã trả cho bảng trên cổng: từng hoá đơn, đã trả,
 * còn nợ, và tổng theo từng loại tiền. Không cộng lại gì ở đây — hai con số khác
 * nhau cho cùng một khoản nợ là chuyện phải tránh trên một tờ giấy gửi ra ngoài.
 * Không quy đổi tiền tệ: hệ không giữ tỷ giá, nên mỗi loại tiền một dòng tổng.
 *
 * Cửa sổ 3 tháng ghi rõ trên giấy, và chứng từ cũ hơn còn số dư được đánh dấu
 * "carried over" — cùng quy ước với màn hình, để nhà cung cấp không tưởng kỳ bị
 * tính sai. Tiếng Anh như mọi bản in gửi ra ngoài của hệ (đơn mua, PI).
 *
 * Phiếu ghi nợ (17/09/2026) in thành dòng riêng, số tiền mang dấu âm, kèm số khiếu nại làm căn cứ;
 * phần đã trừ vào hoá đơn nằm ở cột "Deducted" của hoá đơn đó.
 */
export function buildSupplierStatementHtml(data: SupplierPayables, printedAt = new Date()): string {
  const logoSrc = `${window.location.origin}/brand/hisense-logo.webp`;
  const rows = data.lines
    .map((l, i) => {
      const zebra = i % 2 === 0 ? "white" : "#f5f5f5";
      const debitNote = l.kind === "DebitNote";
      const tags = `${debitNote ? ' <span class="tag dn">debit note</span>' : ""}${l.carriedOver ? ' <span class="tag">carried over</span>' : ""}`;
      const reference = debitNote && l.reference ? `<div class="ref">Claim ${esc(l.reference)}</div>` : "";
      return `<tr style="background-color:${zebra};">
        <td class="mono">${esc(l.documentDate)}</td>
        <td class="mono b">${esc(l.documentNo)}${tags}${reference}</td>
        <td class="c mono">${esc(l.currency)}</td>
        <td class="r mono">${esc(formatMoney(debitNote ? -l.amountMinor : l.amountMinor, l.currency))}</td>
        <td class="r mono">${debitNote ? "—" : esc(formatMoney(l.paidMinor, l.currency))}</td>
        <td class="r mono">${esc(formatMoney(l.deductedMinor, l.currency))}</td>
        <td class="r mono b">${esc(formatMoney(l.balanceMinor, l.currency))}</td></tr>`;
    })
    .join("");
  const totals = data.totals
    .map(
      (t) =>
        `<tr class="grand"><td colspan="6" class="r">TOTAL OUTSTANDING · ${esc(t.currency)}</td><td class="r mono">${esc(formatMoney(t.balanceMinor, t.currency))}</td></tr>`,
    )
    .join("");
  const hasDebitNotes = data.lines.some((l) => l.kind === "DebitNote");

  return `<!doctype html><html><head><meta charset="utf-8"><title>Statement ${esc(data.supplierName)}</title>
<style>
  @page { size: A4; margin: 15mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, "Helvetica Neue", sans-serif; color:#000; font-size:10px; margin:0; }
  @media screen { body { padding: 24px; min-width: 720px; } }
  thead { display: table-header-group; }
  tr { break-inside: avoid; }
  .mono { font-family:"Courier New",monospace; font-variant-numeric:tabular-nums; }
  .c { text-align:center; } .r { text-align:right; } .b { font-weight:bold; }
  .muted { color:#555; }
  .top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:10px; }
  .logo { height:46px; }
  .title { text-align:right; }
  .title h1 { margin:0; font-size:20px; letter-spacing:2px; }
  .title .sub { font-size:11px; font-weight:bold; margin-top:2px; }
  .parties { display:flex; gap:14px; margin:10px 0; }
  .parties > div { flex:1; border:1px solid #000; padding:7px; }
  .parties .h { font-size:8px; letter-spacing:1px; text-transform:uppercase; border-bottom:1px solid #000; margin-bottom:4px; padding-bottom:2px; }
  .parties .n { font-weight:bold; font-size:11px; }
  table { width:100%; border-collapse:collapse; margin-top:8px; }
  th { background:#000; color:#fff; padding:5px 4px; font-size:8px; letter-spacing:.4px; text-transform:uppercase; }
  td { border:1px solid #bbb; padding:4px; vertical-align:top; }
  tfoot td { border:none; padding:3px 4px; font-weight:bold; }
  tfoot tr.grand td { border-top:2px solid #000; font-size:11px; }
  .tag { font-family: Arial, sans-serif; font-size:8px; font-weight:normal; color:#8a5a00; background:#fff3d6; padding:1px 4px; border-radius:3px; }
  .tag.dn { color:#0b4f6c; background:#e0f0f7; }
  .ref { font-family: Arial, sans-serif; font-size:8px; font-weight:normal; color:#555; margin-top:1px; }
  .note { margin-top:12px; border:1px solid #bbb; padding:7px; color:#333; }
  .foot { margin-top:16px; border-top:1px solid #ddd; padding-top:5px; font-size:8px; color:#777; }
</style></head><body>
  <div class="top">
    <div>
      <img class="logo" src="${logoSrc}" alt="">
      <div class="b" style="margin-top:4px;">HISENSE BUTTON CO., LTD.</div>
    </div>
    <div class="title"><h1>STATEMENT OF ACCOUNT</h1><div class="sub">Payables to supplier</div></div>
  </div>

  <div class="parties">
    <div>
      <div class="h">Supplier</div>
      <div class="n">${esc(data.supplierName)}</div>
    </div>
    <div>
      <div class="h">Statement details</div>
      <div><b>Period from:</b> ${esc(data.windowStart)}</div>
      <div><b>Statement date:</b> ${esc(longDate(printedAt))}</div>
      <div><b>Documents listed:</b> ${data.lines.length}</div>
    </div>
  </div>

  <table>
    <thead><tr>
      <th>Date</th><th>Document</th><th>Currency</th><th>Amount</th><th>Paid</th><th>Deducted</th><th>Balance</th>
    </tr></thead>
    <tbody>${rows || '<tr><td colspan="7" class="c muted">Nothing outstanding and nothing invoiced in this period.</td></tr>'}</tbody>
    <tfoot>${totals}</tfoot>
  </table>

  <div class="note">Approved supplier invoices and posted debit notes from ${esc(data.windowStart)} onward. Older documents still appear, marked “carried over”, while they have a balance. Amounts are shown per currency and are never converted.${hasDebitNotes ? " A debit note is a deduction for an approved supplier claim: the part already deducted appears in the invoice’s Deducted column, and a negative balance is the part still to be deducted from a later invoice." : ""}</div>

  <div class="foot">Hisense Button · hisensebutton.toankhontech.com · printed ${esc(printedAt.toLocaleString("en-GB"))}</div>
</body></html>`;
}

export function printSupplierStatement(data: SupplierPayables) {
  printHtml(buildSupplierStatementHtml(data));
}
